import { FC, PropsWithChildren } from 'react';
import styled, { useTheme } from 'styled-components';
import fireBallBoardCard from '../../assets/apng/fireball-board-card.png';
import fireBallSmoke from '../../assets/apng/fireball-smoke.png';
import { useCalled } from '../../hooks/use-called';
import { CardNumberContent, FireBallBoardCardImage } from '../CardNumber75';

export type CardNumberProps = {
    isCalled: boolean;
    autoDaub: boolean;
    bounce?: boolean;
    dauberColor?: string;
    dauberStyle?: string;
    isFireBall?: boolean;
    isFireBallSnuffed?: boolean;
};

export const CardNumber30Container = styled.div<CardNumberProps>`
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    width: 5rem;
    height: 4.5rem;
    border-radius: 0.5rem;
    caret-color: transparent;

    ${({ isCalled, autoDaub, dauberColor, dauberStyle, theme }) => {
        const cardNumber30 = (theme as any).cardNumber30 || theme.cardNumber75;
        return `
            background: ${cardNumber30.background};
            cursor: ${autoDaub ? 'auto' : 'pointer'};
        
            ${
                isCalled
                    ? dauberColor && !dauberStyle
                        ? `background-color: ${dauberColor};`
                        : dauberStyle
                        ? ''
                        : `
                    border: solid 0.094rem ${cardNumber30.border};
                    box-shadow: inset 0 0.25rem 0 0 ${cardNumber30.boxShadow.upper}, inset 0 -0.25rem 0 0 ${cardNumber30.boxShadow.lower};
                    background-image: linear-gradient(to bottom, ${cardNumber30.gradient.top},  ${cardNumber30.gradient.bottom} 99%);
                `
                    : ''
            };
        `;
    }};
`;

export const ContainerStick = styled.div<{ isCalled: boolean }>`
    position: absolute;
    width: 0.5rem;
    border-radius: 0.5rem;
    ${({ isCalled, theme }) => {
        const stickBg =
            (theme as any).cardNumber30?.stickBackground ||
            theme.cardNumber80.stickBackground;
        return isCalled
            ? `
            left: 0;
            margin-left: -0.156rem;
            box-shadow: ${stickBg.boxShadow};
            background-image: ${stickBg.background};
        `
            : `
            right: 0;
            margin-right: -0.156rem;
            box-shadow: ${stickBg.boxShadow};
            background-image: ${stickBg.background};
        `;
    }};
`;

export const CardNumber30Text = styled.div<{ isCalled: boolean }>`
    font-family: Signika-Bold;
    font-size: 2rem;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 0.91;
    letter-spacing: normal;
    text-align: center;
    user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;

    ${({ isCalled, theme }) => {
        const cardNumber30 = (theme as any).cardNumber30 || theme.cardNumber75;
        return `
            color: ${
                isCalled
                    ? cardNumber30.color.isCalled
                    : cardNumber30.color.normal
            };
            opacity: ${isCalled ? '1' : '0.7'};
            text-shadow: ${
                isCalled
                    ? `0.063rem 0.063rem ${cardNumber30.color.normal}`
                    : `0.063rem 0.063rem ${cardNumber30.color.isCalled}`
            };
        `;
    }};
`;

export const CardNumber30Dauber = styled.svg<{ fill: string }>`
    width: 1.5rem !important;
    ${({ fill }) => fill && `fill: ${fill}`};
`;

export const CardNumber30: FC<PropsWithChildren<CardNumberProps>> = ({
    dauberColor,
    dauberStyle,
    bounce,
    children,
    isFireBall,
    isFireBallSnuffed,
    ...props
}) => {
    const theme = useTheme();
    const { daubed, handleClick } = useCalled(
        props.autoDaub,
        props.isCalled,
        children
    );

    const { dauberColor: dauberColorObject } = theme;
    const { dauberStyle: dauberStyleObject } = theme.images;

    return (
        <CardNumber30Container
            {...props}
            className={bounce ? 'bounce' : ''}
            isCalled={daubed}
            dauberStyle={dauberStyle}
            dauberColor={dauberColorObject[dauberColor!]}
            onClick={handleClick}
        >
            <ContainerStick isCalled={daubed} />
            <CardNumberContent>
                {daubed && dauberStyle ? (
                    <CardNumber30Dauber
                        as={dauberStyleObject[dauberStyle!] as any}
                        fill={dauberColorObject[dauberColor!]}
                    />
                ) : (
                    <CardNumber30Text isCalled={daubed}>
                        {children}
                    </CardNumber30Text>
                )}
                {isFireBall && (
                    <FireBallBoardCardImage
                        src={
                            isFireBallSnuffed
                                ? fireBallSmoke
                                : fireBallBoardCard
                        }
                        alt=""
                    />
                )}
            </CardNumberContent>
        </CardNumber30Container>
    );
};
